/**
 * Codex PR #784 round 7 P2 — THE refined single-point entity pick.
 *
 * One function, used by EVERY command that answers "which entity is under this
 * world point": the LMB click-pick (`StagePointerHandlers.handleClickPick`) and
 * the `ZOOM Object` pick (`stageInteractions.ts`, through the picker the host
 * publishes — see {@link RefinedEntityPicker}). Extracted so the two can never
 * drift: before this module `ZOOM Object` called `SpatialIndex.pick`, which
 * answers from the BBOX broad phase alone (smallest intersecting AABB wins),
 * with NO wrapper lift and NO child-distance refinement. Since a Block's
 * metadata record now spans its full definition bounds (not just the insertion
 * point), clicking the EMPTY interior of a sparse INSERT's box selected — and
 * zoomed to — that INSERT, while clicking one of its visible strokes could miss
 * it entirely.
 *
 * The pipeline, in order (each step's WHY is on the step):
 *
 *   1. `intersect` — RBush AABB broad phase around the point ± tolerance.
 *   2. Space eligibility + VPLAYER (per-viewport freeze) rejection.
 *   3. `filterHittableEntities` — LOCKED / globally off-or-frozen layers, over
 *      the candidate's own layer AND its block-reference layer chain.
 *   4. Cache-direct LIFT — a metadata stub is replaced by the renderable the
 *      renderer draws (an INSERT → its `block-insert` wrapper), so the distance
 *      below is measured against REAL geometry instead of the stub's bbox
 *      centre. Annotatively hidden entities drop out here.
 *   5. DRAWORDER sort (zOrder ASC, id ASC) so the refinement's "last wins on
 *      tie" rule reproduces AutoCAD's topmost pick.
 *   6. `refinePickCandidates` with the visible-child-geometry filter — ONE
 *      distance walk producing both the winner and every candidate genuinely
 *      under the cursor (the SELECTIONCYCLING list).
 *
 * AutoCAD parity note: steps 2-4 are exactly the gates the click path applies,
 * and `ZOOM Object` in AutoCAD prompts "Select objects" — i.e. it uses the SAME
 * object-selection rules, so an object on a locked / off / frozen layer is no
 * more selectable there than under a plain click.
 */

import type { EntityPreview } from "@dxf-ai-editor/contracts";

import type { AdapterContext } from "../adapters";
import {
  typedAnnotativeHidden,
  type TypedEntityResolver,
} from "../adapters/typedFirstRenderable";
import {
  isHiddenByLayerChain,
  makeVisibleChildGeometryFilter,
} from "../layerChainVisibility";
import { refinePickCandidates, type RefinedPickResult } from "../raycast/pick";
import { filterHittableEntities } from "../state/layerVisibility";
import { liftRenderableForId } from "../state/spatialBuilder";
import type { VisibleSetCache } from "../state/visibleSetCache";
import type { RenderableEntity, SpatialIndex, Vec2 } from "../types";

/** Everything {@link refineEntityPick} needs, resolved by the caller per pick. */
export interface RefinedEntityPickDeps {
  /** The live index (the render pass's, so already-lifted entities resolve as such). */
  readonly spatialIndex: SpatialIndex;
  /** LAYER-locked layers — un-pickable, though still drawn (AutoCAD parity). */
  readonly lockedLayers: ReadonlySet<string>;
  /** Globally off / frozen layers — invisible, hence un-pickable. */
  readonly hiddenLayers: ReadonlySet<string>;
  /**
   * The ACTIVE viewport's VPLAYER-frozen layers, or `null` outside a redirected
   * in-VP pick. Rejects a candidate frozen in THIS viewport and hides that
   * layer's child geometry inside an INSERT.
   */
  readonly vpFrozenLayers: ReadonlySet<string> | null;
  /**
   * Space gate — `false` drops a candidate belonging to another space. Inside a
   * VP this is what keeps a click through the viewport from picking the paper
   * sheet's own geometry (all spaces share ONE index). Identity callers pass a
   * predicate that is always `true`.
   */
  readonly candidateEligible: (spaceId: string) => boolean;
  /** Cache-direct preview cache; `null` disables the stub lift (legacy branch). */
  readonly cache: VisibleSetCache | null;
  readonly buildPreview: (id: string) => EntityPreview | null;
  /** DEFUSE S11 typed resolver backing the typed-first lift. */
  readonly resolveEntity: TypedEntityResolver;
  readonly adapterCtxBase: AdapterContext;
}

/**
 * The picker a host publishes for callers that cannot see the cache / typed
 * resolver / adapter context themselves (`ZOOM Object`, which runs in
 * `stageInteractions.ts` OUTSIDE the R3F canvas). Returns the picked entity id
 * — always the spatial-index entity's own id, never a satellite child's — or
 * `null` for an empty pick.
 *
 * Codex PR #784 round 8 P2 — both arguments are in the MAIN (paper/model)
 * CAMERA's world space, i.e. exactly what `clientToWorld` yields for the
 * cursor, because the main camera never dives into a viewport
 * (`viewportContextController`: "On enter: NO camera change"). The publisher
 * (`StagePointerHandlers.installStageClickHandlers`) resolves the active input
 * space from that point per call — the paper→VP-model redirect, the
 * active-space `candidateEligible` gate, the VP-model aperture and the active
 * viewport's VPLAYER-frozen set — so callers must NOT pre-transform the point
 * or pre-scale the tolerance.
 */
export type RefinedEntityPicker = (
  /** Cursor point in MAIN-camera world coords (paper coords on a layout). */
  paperPt: Vec2,
  /** Pick tolerance in those same MAIN-camera world units. */
  paperToleranceWorld: number,
) => string | null;

/**
 * Resolve the entity under `world` within `toleranceWorld`, returning BOTH the
 * winner and every candidate the same distance walk found under the cursor
 * (`within` — the SELECTIONCYCLING stacked list; see {@link RefinedPickResult}).
 */
export function refineEntityPick(
  world: Vec2,
  toleranceWorld: number,
  deps: RefinedEntityPickDeps,
): RefinedPickResult {
  const candidateIds = deps.spatialIndex.intersect({
    left: world.x - toleranceWorld,
    top: world.y - toleranceWorld,
    right: world.x + toleranceWorld,
    bottom: world.y + toleranceWorld,
  });
  const vpFrozenSet =
    deps.vpFrozenLayers !== null && deps.vpFrozenLayers.size > 0
      ? deps.vpFrozenLayers
      : null;
  // Opt 3 — the index returns ids; resolve through its entity resolver so the
  // downstream layer-visibility / cache-direct lift paths see full
  // `RenderableEntity` values. Stale ids (resolver returns undefined) are
  // skipped silently.
  //
  // MC4 S2 — drop candidates the active space rejects. Inside a VP the spatial
  // index still holds BOTH paper- and model-space entities (all spaces share
  // one index); `candidateEligible` keeps only model-space ones so a click
  // through the VP cannot pick the paper sheet's title-block etc. In the
  // identity case `candidateEligible` is always true → no-op filter.
  //
  // MC4 S2 P2 (#D) — ALSO drop candidates frozen IN THE ACTIVE VIEWPORT. The
  // active paper-space VP can freeze layers independently of the global LAYER
  // state (`ViewportEntity.frozenLayers`; the cull/render path hides them — see
  // `viewportCullingAdapter`). A model entity on a VP-frozen layer is invisible
  // through the VP, so it must be un-pickable there too (AutoCAD parity).
  const candidates: RenderableEntity[] = [];
  for (const id of candidateIds) {
    const resolved = deps.spatialIndex.resolve(id);
    // VPLAYER-frozen pre-filter over the candidate's own layer AND its
    // block-reference layer chain (Codex PR #784 round 3 P1 — a legacy-branch
    // block child is its own index entry and must not pick when its INSERT's
    // layer is frozen in this viewport).
    if (
      resolved &&
      deps.candidateEligible(resolved.spaceId) &&
      !(
        vpFrozenSet !== null &&
        isHiddenByLayerChain(
          resolved.layer,
          resolved.renderParentLayers,
          vpFrozenSet,
        )
      )
    ) {
      candidates.push(resolved);
    }
  }
  const filtered = filterHittableEntities(
    candidates,
    deps.lockedLayers,
    deps.hiddenLayers,
  );
  const lifted = liftPickCandidates(filtered, deps);
  // Sort by (zOrder, id) ascending so refinePickCandidates' "last wins on tie"
  // rule produces the same DRAWORDER-aware topmost pick.
  const sorted = lifted.sort((a, b) => {
    if (a.zOrder !== b.zOrder) return a.zOrder - b.zOrder;
    return a.id < b.id ? -1 : a.id > b.id ? 1 : 0;
  });
  // Codex PR #784 round 3 P2 — an INSERT's hidden children contribute no drawn
  // geometry, so they must not make the reference pickable. Same truth the
  // render path uses (global off / frozen layers + this VP's frozen set);
  // `null` when nothing is hidden, and the per-child check is then skipped.
  const childFilter = makeVisibleChildGeometryFilter(
    deps.hiddenLayers,
    vpFrozenSet,
  );
  // Codex PR #784 round 4 F3 — ONE distance-refinement walk drives BOTH the
  // primary pick and the SELECTIONCYCLING stacked list (`within`), so every
  // candidate's `distanceToRenderable` is evaluated exactly once and the two
  // can never disagree about what is under the cursor.
  return refinePickCandidates(world, toleranceWorld, sorted, childFilter);
}

/**
 * PR-V2 — in cache-direct mode the spatial index holds metadata stubs with
 * degenerate POINT geometry, so each candidate is lifted through the cache and
 * `distanceToRenderable` sees the real geometry.
 */
function liftPickCandidates(
  filtered: readonly RenderableEntity[],
  deps: RefinedEntityPickDeps,
): RenderableEntity[] {
  const lifted: RenderableEntity[] = [];
  for (const c of filtered) {
    const typed = deps.resolveEntity(c.id);
    // S10d (round-18 FINDING, typedFirstRenderable:127 pick path) — an
    // annotative TEXT/MTEXT/HATCH hidden at the active CANNOSCALE
    // (ANNOALLVISIBLE=false, no matching representation) is NOT rendered, so it
    // must NOT be pickable via its cache-direct metadata stub either. The
    // render lift below returns `null` for it, but the pre-existing fallback
    // (`lifted.push(c)`) would re-admit the raw stub. Drop it up front — same
    // visibility truth the render gate uses (`typedAnnotativeHidden`).
    if (typed && typedAnnotativeHidden(typed, deps.adapterCtxBase)) {
      continue;
    }
    // Codex PR #784 round 3 P1 — COST NOTE: this runs per pick candidate, so an
    // INSERT the render pass already lifted is REUSED rather than re-flattened.
    // The spatial index's entity resolver is bound to that pass's
    // `renderableById` (`finishSceneBulkLoad`), so such a candidate arrives
    // here ALREADY as its `block-insert` wrapper — real geometry, hence not a
    // stub — and skips the lift below entirely. Only a candidate the registry
    // could not hand over as real geometry (a raw metadata stub) pays the lift.
    // NOT `state/metadataStub.ts isMetadataStubRenderable` — this predicate is
    // DELIBERATELY WIDER. It is a cost heuristic ("might this single-point
    // record need a cache lift?"), so it also counts a GENUINE DXF POINT with a
    // non-degenerate PDSIZE marker bbox; the only cost of that over-reach is a
    // redundant lift. `isMetadataStubRenderable` is a CORRECTNESS gate (`type
    // !== "Point"`) used by OSNAP / window-select to suppress a record that
    // draws nothing. Do not merge the two: widening the OSNAP gate to this one
    // would kill NODe snapping on real POINT entities.
    const isStub =
      c.geometry.kind === "point" &&
      (c.bounds.maxX - c.bounds.minX > 0 || c.bounds.maxY - c.bounds.minY > 0);
    if (isStub && deps.cache) {
      // Codex PR #784 round 3 P1 — lift through `liftRenderableForId`, the SAME
      // seam the renderer and the FrameLoops relift use, NOT
      // `renderableFromDrawingEntity` directly. Its `Block` arm returns `null`
      // (an INSERT flattens to MANY renderables), so the previous direct call
      // fell back to the metadata stub and the pick distance was measured from
      // the stub's bbox CENTRE — a click on a visible child stroke away from
      // that centre could not hit the INSERT, and a click in the empty interior
      // of a sparse block did. `liftRenderableForId` dispatches Block to
      // `liftBlockInsertRenderable` (with `ctx.blocks`), yielding the wrapper —
      // keeping the INSERT's own id, never exposing a child id — so
      // `distanceToRenderable`'s `block-insert` arm measures the real strokes.
      const liftedRenderable = liftRenderableForId({
        id: c.id,
        cache: deps.cache,
        buildPreview: deps.buildPreview,
        resolveEntity: deps.resolveEntity,
        ctx: {
          ...deps.adapterCtxBase,
          zOrderIndex: c.zOrder,
        },
      });
      if (liftedRenderable) {
        lifted.push(liftedRenderable);
        continue;
      }
    }
    lifted.push(c);
  }
  return lifted;
}
